<!-- Bell Icon Button - visible if user has view notification permission -->
<button *ngIf="hasViewPermission"
        mat-icon-button
        [matMenuTriggerFor]="notificationMenu"
        [matBadge]="count"
        [matBadgeHidden]="count === 0"
        matBadgeColor="warn"
        matBadgeSize="small"
        matTooltip="Notifications">
    <mat-icon [svgIcon]="'tabler_outline:bell-ringing'"
              class="text-amber-500"></mat-icon>
</button>

<!-- Dropdown Menu -->
<mat-menu #notificationMenu="matMenu" class="notification-menu" panelClass="!max-w-[420px] !w-[420px] !rounded-2xl" xPosition="after">
    <div class="w-full flex flex-col" (click)="$event.stopPropagation()">

        <!-- Empty state -->
        <div *ngIf="count === 0" class="px-4 py-6 text-center">
            <p class="text-sm text-gray-400 dark:text-gray-500">No notifications</p>
        </div>

        <!-- Notification list -->
        <div *ngIf="count > 0" class="p-3 space-y-3">
            
            <!-- Pending Project Requests -->
            <div *ngIf="counters.pendingProjectRequestsCount > 0"
                 class="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 border border-blue-400 dark:border-blue-500 cursor-pointer transition-all duration-300 group"
                 (click)="navigateAndDismiss(['/account/projects/requests'])">
                <div class="flex-1 min-w-0">
                    <p class="text-[15px] font-bold text-slate-800 dark:text-slate-100">Project Requests</p>
                    <p class="text-xs font-semibold text-blue-600 dark:text-blue-400 mt-1">{{ counters.pendingProjectRequestsCount }} pending approval</p>
                </div>
            </div>

            <!-- Pending Markout Requests -->
            <div *ngIf="counters.pendingMarkoutRequestsCount > 0"
                 class="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 border border-purple-400 dark:border-purple-500 cursor-pointer transition-all duration-300 group"
                 (click)="navigateAndDismiss(['/account/markouts/requests'])">
                <div class="flex-1 min-w-0">
                    <p class="text-[15px] font-bold text-slate-800 dark:text-slate-100">Markout Requests</p>
                    <p class="text-xs font-semibold text-purple-600 dark:text-purple-400 mt-1">{{ counters.pendingMarkoutRequestsCount }} pending approval</p>
                </div>
            </div>

            <!-- Expiring Markouts -->
            <div *ngIf="counters.expiringCount > 0 && !isExpiringDismissed"
                 class="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 border border-amber-400 dark:border-amber-500 cursor-pointer transition-all duration-300 group"
                 (click)="navigateAndDismiss(['/account/markouts/map'])">
                <div class="flex-1 min-w-0">
                    <p class="text-[15px] font-bold text-slate-800 dark:text-slate-100">Expiring Markouts</p>
                    <p class="text-xs font-semibold text-amber-600 dark:text-amber-400 mt-1">{{ counters.expiringCount }} expiring soon</p>
                </div>
                <button mat-icon-button
                        class="!h-6 !w-6 flex-shrink-0 text-gray-300 hover:text-gray-500 dark:hover:text-gray-300"
                        matTooltip="Dismiss"
                        (click)="dismissExpiring($event)">
                    <mat-icon svgIcon="tabler_outline:x" class="icon-size-3"></mat-icon>
                </button>
            </div>

            <!-- Expired Markouts -->
            <div *ngIf="counters.expiredCount > 0 && !isExpiredDismissed"
                 class="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 border border-red-400 dark:border-red-500 cursor-pointer transition-all duration-300 group"
                 (click)="navigateAndDismiss(['/account/markouts/map'])">
                <div class="flex-1 min-w-0">
                    <p class="text-[15px] font-bold text-slate-800 dark:text-slate-100">Expired Markouts</p>
                    <p class="text-xs font-semibold text-red-600 dark:text-red-400 mt-1">{{ counters.expiredCount }} expired tickets</p>
                </div>
                <button mat-icon-button
                        class="!h-6 !w-6 flex-shrink-0 text-gray-300 hover:text-gray-500 dark:hover:text-gray-300"
                        matTooltip="Dismiss"
                        (click)="dismissExpired($event)">
                    <mat-icon svgIcon="tabler_outline:x" class="icon-size-3"></mat-icon>
                </button>
            </div>

            <!-- Expiring Documents Section -->
            <ng-container *ngIf="expiringDocuments.length > 0">
                <div class="pt-4 pb-1 px-2">
                    <p class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Document Updates Required</p>
                </div>

                <div *ngFor="let doc of expiringDocuments"
                     class="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 border border-orange-400 dark:border-orange-500 cursor-pointer transition-all duration-300 group"
                     (click)="navigateToDocument(doc)">
                    <div class="flex-1 min-w-0">
                        <p class="text-[15px] font-bold text-slate-800 dark:text-slate-100">{{ doc.name }}</p>
                        <p class="text-xs font-semibold text-orange-600 dark:text-orange-400 mt-1">
                            Expires: {{ doc.expirationDate | date:'MMM d, yyyy' }}
                            <span class="text-gray-400 ml-1">• {{ doc.type === 'OUR_DOCUMENTS' ? 'Company' : 'Subcontractor' }}</span>
                        </p>
                    </div>
                    <mat-icon svgIcon="tabler_outline:chevron-right" class="icon-size-4 text-gray-300 group-hover:text-orange-500 transition-colors"></mat-icon>
                </div>
            </ng-container>

            <!-- Expiring Equipment Documents Section -->
            <ng-container *ngIf="expiringEquipmentDocuments.length > 0">
                <div class="pt-4 pb-1 px-2 border-t border-gray-150 dark:border-gray-800">
                    <p class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Equipment Document Updates Required</p>
                </div>

                <div *ngFor="let doc of expiringEquipmentDocuments"
                     class="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 border border-orange-400 dark:border-orange-500 cursor-pointer transition-all duration-300 group"
                     (click)="navigateToEquipmentDocument(doc)">
                    <div class="flex-1 min-w-0">
                        <p class="text-[15px] font-bold text-slate-800 dark:text-slate-100">{{ doc.name }} ({{ doc.equipmentName }})</p>
                        <p class="text-xs font-semibold text-orange-600 dark:text-orange-400 mt-1">
                            Expires: {{ doc.expirationDate | date:'MMM d, yyyy' }}
                        </p>
                    </div>
                    <mat-icon svgIcon="tabler_outline:chevron-right" class="icon-size-4 text-gray-300 group-hover:text-orange-500 transition-colors"></mat-icon>
                </div>
            </ng-container>


            <!-- Planner Reminders Section -->
            <ng-container *ngIf="plannerReminders.length > 0">
                <div class="pt-4 pb-1 px-2 border-t border-gray-150 dark:border-gray-800">
                    <p class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Planner Reminders</p>
                </div>

                <div *ngFor="let rem of plannerReminders"
                     class="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 border border-indigo-400 dark:border-indigo-500 cursor-pointer transition-all duration-300 group"
                     (click)="openReminder(rem)">
                    <div class="flex-1 min-w-0">
                        <p class="text-[15px] font-bold text-slate-800 dark:text-slate-100">{{ rem.title || 'Untitled Schedule' }}</p>
                        <p class="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mt-1">
                            Date: {{ rem.fromDate | date:'MMM d, yyyy' }} at {{ rem.reminderTime || '00:00' }}
                        </p>
                    </div>
                    <mat-icon svgIcon="tabler_outline:chevron-right" class="icon-size-4 text-gray-300 group-hover:text-indigo-500 transition-colors"></mat-icon>
                </div>
            </ng-container>

            <!-- Modified Timesheets Section Header -->
            <div *ngIf="notifications.length > 0" class="pt-4 pb-1 px-2">
                <p class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Modified Timesheets</p>
            </div>

            <div *ngFor="let n of notifications"
                 class="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 border border-emerald-400 dark:border-emerald-500 cursor-pointer transition-all duration-300 group"
                 (click)="openTimesheet(n)">
                <div class="flex-1 min-w-0">
                    <p class="text-[15px] font-bold text-slate-800 dark:text-slate-100">{{ n.projectName }}</p>
                    <p class="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mt-1">{{ formatTsDate(n.date) }} • by {{ n.lastModifiedByName || 'unknown' }}</p>
                </div>
                <button mat-icon-button
                        class="!h-6 !w-6 flex-shrink-0 text-gray-300 hover:text-gray-500 dark:hover:text-gray-300"
                        matTooltip="Dismiss"
                        (click)="dismiss(n, $event)">
                    <mat-icon svgIcon="tabler_outline:x" class="icon-size-3"></mat-icon>
                </button>
            </div>
        </div>

        <!-- Dismiss all -->
        <div *ngIf="notifications.length > 1" class="border-t border-gray-100 dark:border-gray-700 px-4 py-2 text-center">
            <button mat-button class="!text-xs !min-h-0 !h-7 text-primary-600 dark:text-primary-400" (click)="dismissAll()">Dismiss Timesheet Edits</button>
        </div>
    </div>
</mat-menu>
